"use client"

import { Content, Heading } from 'mdast';
import { Hourglass, Eye, Waypoints, RotateCcw, type LucideIcon } from 'lucide-react';
import { ChunkDocContext } from '@/lib/parseMarkdown';
import { FootnoteHandler } from '../Markdown';

// ─── Editable content ─────────────────────────────────────────────────────────
//
// The four principle rows are authored in the markdown body of the
// `:::corePrinciples` directive, NOT here. Each row is one `## Title` heading
// whose body begins with an `icon: Name` line, followed by the description:
//
//     :::corePrinciples
//     ## Buy time
//     icon: Hourglass
//     Slow the intelligence explosion as much as we realistically can.
//
//     ## Make all AI research public
//     icon: Eye
//     Enforce research transparency and audit all frontier training runs.
//     :::
//
//   • `## Title`   — serif title shown on the row.
//   • `icon: Name` — selects the lucide icon (see CORE_PRINCIPLE_ICONS).
//                    Consumed by the parser, not displayed. Case-insensitive.
//   • everything   — the greyed Gill-Sans description next to the title.
//     after
//
// Descriptions render as plain text (the current four have no links or
// footnotes). docContext/FootnoteHandler are threaded through anyway so the
// description could switch to <Markdown> later with a one-line change.

// Icon name → lucide component. Reached by one cmd-click from the render site
// (`CORE_PRINCIPLE_ICONS[item.icon]`), per the project's separated-content rule.
// Keys are matched case-insensitively against the `icon: Name` line.
export const CORE_PRINCIPLE_ICONS: Record<string, LucideIcon> = {
  Hourglass,
  Eye,
  Waypoints,
  RotateCcw,
};

type Principle = {
  /** Icon registry key parsed from the `icon: Name` line (may be ''). */
  icon: string;
  /** From `## Title`. */
  title: string;
  /** The body text after the `icon:` line — the greyed description. */
  desc: string;
};

function getTextFromNode(node: Content): string {
  if (node.type === 'text') return (node as any).value;
  if ('children' in node && Array.isArray((node as any).children)) {
    return (node as any).children.map(getTextFromNode).join('');
  }
  return '';
}

// Matches a leading `icon: Name` line. In a Google Doc the `icon:` line and the
// description usually end up in one paragraph (no blank line between them), so
// the body text arrives as e.g. "icon: Hourglass\nSlow the intelligence…". We
// strip the leading icon line and keep the rest as the description.
const LEADING_ICON = /^[ \t]*icon:[ \t]*([a-z0-9]+)[ \t]*(?:\r?\n|$)/i;

// Resolve an author-typed icon name to a registry key (case-insensitive),
// returning '' if unknown.
function resolveIcon(name: string): string {
  const requested = name.toLowerCase();
  return Object.keys(CORE_PRINCIPLE_ICONS).find((k) => k.toLowerCase() === requested) ?? '';
}

// A `## ` (depth-2) heading starts a row; the body nodes that follow until the
// next `## ` make up that row. A leading `icon: Name` line (whether its own
// paragraph or the first line of the description paragraph) sets the icon and is
// removed from the description text. (Mirrors parsePolicyList in
// IncrementalistWishlistDiagram.)
function parseCorePrinciples(children: Content[]): Principle[] {
  const items: Principle[] = [];
  let current: Principle | null = null;

  for (const node of children) {
    const heading = node.type === 'heading' ? (node as Heading) : null;

    if (heading?.depth === 2) {
      if (current) items.push(current);
      current = { icon: '', title: getTextFromNode(node).trim(), desc: '' };
      continue;
    }

    if (!current) continue;

    let text = getTextFromNode(node).trim();
    if (!text) continue;

    // Pull a leading `icon: Name` line off the start of the first body text.
    if (!current.icon && !current.desc) {
      const iconMatch = text.match(LEADING_ICON);
      if (iconMatch) {
        current.icon = resolveIcon(iconMatch[1]);
        text = text.slice(iconMatch[0].length).trim();
        if (!text) continue;
      }
    }

    current.desc = current.desc ? `${current.desc} ${text}` : text;
  }
  if (current) items.push(current);
  return items;
}

interface PlanPrinciplesBoxProps {
  children?: Content[];
  // docContext / FootnoteHandler are accepted to mirror the sibling directive
  // blocks; currently unused because descriptions render as plain text.
  docContext?: ChunkDocContext;
  FootnoteHandler?: FootnoteHandler;
}

export const PlanPrinciplesBox = ({ children = [] }: PlanPrinciplesBoxProps) => {
  const items = parseCorePrinciples(children);
  if (items.length === 0) return null;

  return (
    /* `plan-principles-box` is a print.css hook (the card list must not split
       across a page turn); it carries no on-site styling. */
    <div className="plan-principles-box my-8 text-[#14202e] font-['et-book',Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif]">
      <div className="rounded-[8px] border-[1.5px] border-[#1a1a1a] bg-vivid-background px-4 py-0.5">
        {items.map((item) => {
          const IconComponent = CORE_PRINCIPLE_ICONS[item.icon];
          return (
            <div
              key={item.title}
              className="flex items-start gap-3.5 py-[13px] [&:not(:first-child)]:border-t [&:not(:first-child)]:border-[#1a1a1a]"
            >
              <span
                className="mt-px h-[26px] w-[26px] shrink-0 text-[#8B0000] [&_svg]:block [&_svg]:h-full [&_svg]:w-full"
                aria-hidden="true"
              >
                {IconComponent && <IconComponent strokeWidth={2} />}
              </span>
              <div className="min-w-0 flex-1 leading-[1.35]">
                <span className="mr-[9px] text-[20px] font-bold text-[#14202e]">{item.title}</span>
                <span className="text-[15px] text-[#8b897f] font-['Gill_Sans','Gill_Sans_MT','Gill_Sans_Nova',Calibri,'Trebuchet_MS',sans-serif]">
                  {item.desc}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
